/**
 * 多语言国际化模块
 * 支持动态加载和切换语言
 */

// 直接导入 JSON 文件（Parcel 会自动处理）
import enTranslations from '../data/en.json';
import zhTranslations from '../data/zh.json';

class I18n {
  constructor() {
    // 优先读取 HTML 的 lang 属性作为默认语言（构建版本会设置这个）
    // 如果没有，则读取 localStorage 中的保存值
    // 最后默认使用英文
    const htmlLang = document.documentElement.lang || document.documentElement.getAttribute('lang');
    const savedLang = localStorage.getItem('language');
    
    // 如果 HTML lang 属性存在且有效，优先使用它（构建版本）
    if (htmlLang && (htmlLang === 'en' || htmlLang === 'zh')) {
      this.currentLang = htmlLang;
      // 同步到 localStorage，确保一致性
      if (savedLang !== htmlLang) {
        localStorage.setItem('language', htmlLang);
      }
    } else {
      // 否则使用 localStorage 中的值或默认英文
      this.currentLang = savedLang || 'en';
    }
    
    this.translations = {
      en: enTranslations,
      zh: zhTranslations
    };
    this.init();
  }

  emitLanguageChanged(previousLang = null) {
    document.dispatchEvent(new CustomEvent('app:language-changed', {
      detail: {
        lang: this.currentLang,
        previousLang
      }
    }));
  }

  async init() {
    // 等待 DOM 加载完成
    if (document.readyState === 'loading') {
      await new Promise(resolve => {
        document.addEventListener('DOMContentLoaded', resolve);
      });
    }
    
    // 确保翻译已加载，如果找不到则使用默认的英文
    if (!this.translations[this.currentLang]) {
      console.warn(`Translations for ${this.currentLang} not found, using 'en'`);
      this.currentLang = 'en';
    }
    
    // 设置 HTML 的 lang 属性
    document.documentElement.lang = this.currentLang;
    
    this.updatePage();
    this.setupLanguageSwitcher();
    this.emitLanguageChanged();
  }

  t(key) {
    // 检查翻译是否已加载
    if (!this.translations[this.currentLang]) {
      console.warn(`Translations for ${this.currentLang} not loaded yet`);
      return key;
    }
    
    const keys = key.split('.');
    let value = this.translations[this.currentLang];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key} in ${this.currentLang}`);
        return key; // 如果找不到翻译，返回key本身
      }
    }
    
    return value || key;
  }

  async switchLanguage(lang) {
    if (lang === this.currentLang) return;
    
    if (!this.translations[lang]) {
      console.warn(`Translations for ${lang} not found`);
      return;
    }
    
    const previousLang = this.currentLang;
    this.currentLang = lang;
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
    this.updatePage();
    this.emitLanguageChanged(previousLang);
  }

  updatePage() {
    // 更新页面标题和meta描述
    const title = this.t('meta.title');
    const description = this.t('meta.description');
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', description);
    }

    // 更新 title 标签（特殊处理）
    const titleElement = document.querySelector('title[data-i18n]');
    if (titleElement) {
      titleElement.textContent = title;
    }

    // 更新所有带有 data-i18n 属性的元素（排除 title）
    document.querySelectorAll('[data-i18n]:not(title)').forEach(element => {
      const key = element.getAttribute('data-i18n');
      const translation = this.t(key);
      
      // 如果翻译包含HTML标签，使用innerHTML，否则使用textContent
      if (translation.includes('<')) {
        element.innerHTML = translation;
      } else {
        element.textContent = translation;
      }
    });

    // 更新所有带有 data-i18n-attr 属性的元素（用于更新属性值）
    document.querySelectorAll('[data-i18n-attr]').forEach(element => {
      const attrData = element.getAttribute('data-i18n-attr');
      if (!attrData) return;
      
      const [attr, key] = attrData.split(':');
      if (!attr || !key) {
        console.warn(`Invalid data-i18n-attr format: ${attrData}`);
        return;
      }
      
      const translation = this.t(key);
      // 确保翻译值存在且不是 key 本身（说明找到了翻译）
      if (translation && translation !== key && typeof translation === 'string') {
        element.setAttribute(attr, translation);
        // 调试信息：仅在开发环境输出
        if (process.env.NODE_ENV === 'development') {
          console.log(`Updated ${attr} to "${translation}" for key "${key}" (lang: ${this.currentLang})`);
        }
      } else {
        console.warn(`Failed to update attribute ${attr} for key ${key}, translation: ${translation}, currentLang: ${this.currentLang}`);
        // 如果翻译失败，尝试直接使用 key 作为后备
        if (key === 'about.resumeUrl') {
          const fallbackUrl = this.currentLang === 'zh' ? '/assets/resumeCN.pdf' : '/assets/resumeEN.pdf';
          element.setAttribute(attr, fallbackUrl);
          console.warn(`Using fallback URL: ${fallbackUrl}`);
        }
      }
    });

    // 特殊处理：更新新闻条目列表
    this.updateNewsItems();

    // 更新语言切换按钮文本
    const langSwitcher = document.getElementById('lang-switcher');
    if (langSwitcher) {
      langSwitcher.textContent = this.currentLang === 'en' 
        ? this.t('nav.chinese') 
        : this.t('nav.english');
    }
  }

  updateNewsItems() {
    const newsList = document.querySelector('#news .publication-list');
    if (!newsList) return;

    const newsItems = this.t('news.items');
    if (!Array.isArray(newsItems)) return;

    // 清空现有内容
    newsList.innerHTML = '';

    // 为每个新闻条目创建li元素
    newsItems.forEach((item, index) => {
      const li = document.createElement('li');
      li.className = 'mb-4';

      let html = item.text + ' ';

      // 处理第一个链接和日期
      if (item.report) {
        const href = this.getNewsLink(index, 'report');
        html += `<a href="${href}" target="_blank">${item.report}</a> `;
        // 优先使用date1，如果没有则使用date
        if (item.date1) {
          html += `<span class="date-badge">${item.date1}</span>`;
        } else if (item.date) {
          html += `<span class="date-badge">${item.date}</span>`;
        }
      } else if (item.link) {
        const href = this.getNewsLink(index, 'link');
        html += `<a href="${href}" target="_blank">${item.link}</a> `;
        if (item.date) {
          html += `<span class="date-badge">${item.date}</span>`;
        }
      } else if (item.video) {
        const href = this.getNewsLink(index, 'video');
        html += `<a href="${href}" target="_blank">${item.video}</a> `;
        if (item.date) {
          html += `<span class="date-badge">${item.date}</span>`;
        }
      }

      // 处理多个链接的情况
      if (item.report1) {
        const href1 = this.getNewsLink(index, 'report1');
        html += ` <a href="${href1}" target="_blank">${item.report1}</a> `;
        if (item.date1) {
          html += `<span class="date-badge">${item.date1}</span>`;
        }
      }

      if (item.report2) {
        const href2 = this.getNewsLink(index, 'report2');
        html += ` <a href="${href2}" target="_blank">${item.report2}</a> `;
        if (item.date2) {
          html += `<span class="date-badge">${item.date2}</span>`;
        }
      }

      if (item.preview) {
        const href = this.getNewsLink(index, 'preview');
        html += ` <a href="${href}" target="_blank">${item.preview}</a> `;
        if (item.date2) {
          html += `<span class="date-badge">${item.date2}</span>`;
        }
      }

      if (item.admissionResult) {
        const href = this.getNewsLink(index, 'admissionResult');
        html += ` <a href="${href}" target="_blank">${item.admissionResult}</a> `;
        if (item.date1) {
          html += `<span class="date-badge">${item.date1}</span>`;
        }
      }

      if (item.outstandingList) {
        const href = this.getNewsLink(index, 'outstandingList');
        html += ` <a href="${href}" target="_blank">${item.outstandingList}</a> `;
        if (item.date2) {
          html += `<span class="date-badge">${item.date2}</span>`;
        }
      }

      if (item.admissionNotice) {
        const href = this.getNewsLink(index, 'admissionNotice');
        html += ` <a href="${href}" target="_blank">${item.admissionNotice}</a> `;
        if (item.date3) {
          html += `<span class="date-badge">${item.date3}</span>`;
        }
      }

      li.innerHTML = html;
      newsList.appendChild(li);
    });
  }

  getNewsLink(index, linkType) {
    // 新闻链接的映射（保持原有的链接）
    const links = [
      { report: 'https://mp.weixin.qq.com/s/gqq-9CuhIzy-mu4-lR1-Sg' },
      { report: 'https://mp.weixin.qq.com/s/Ktups4nE-YHXYP-nofzrDA' },
      { 
        report1: 'https://mp.weixin.qq.com/s?__biz=MzU2MTEwNjk4Mg==&mid=2247511318&idx=1&sn=b5546bfd9297ea5b59c9783b029fdd97&chksm=fdfe9ab3e9360031e770994c3f90884d74dc115da56beaf9ffa8a253e6bbe5248057db2470da&scene=27',
        report2: 'https://ah.ifeng.com/c/8lY0qeA5bo4'
      },
      { video: 'https://www.douyin.com/video/7483148356718497064' },
      {
        report: 'https://eecs.pku.edu.cn/info/1040/6730.htm',
        preview: 'https://mp.weixin.qq.com/s?__biz=MzA3ODk4MTYwOA==&mid=2649928445&idx=2&sn=8d61a5d3187bde6a431652a0ebaea4a9&chksm=86694dc678b95d33063454f4536199b89aabc29dd24e0372b7f64b5fe95536bef4f60ca41467&scene=27'
      },
      { report: 'https://mp.weixin.qq.com/s?__biz=MzU2MTEwNjk4Mg==&mid=2247505800&idx=1&sn=e827a07bd7334a0ecc60bc21164c78be&chksm=fd0f659b296251b4541a9fdd39b9e3970a99896373d56baccc9fdf47e6ec8bc543f894a3c64f&scene=27' },
      { link: 'https://bksy.cug.edu.cn/info/2213/8802.htm' },
      { link: 'https://admission.pku.edu.cn/docs/20221101085708238819.pdf' },
      { link: 'https://cs.cug.edu.cn/info/1042/4420.htm' },
      { 
        admissionResult: 'https://cs.pku.edu.cn/info/1022/2496.htm',
        outstandingList: 'https://cs.pku.edu.cn/info/1022/2488.htm',
        admissionNotice: 'https://cs.pku.edu.cn/info/1022/2485.htm'
      },
      { link: 'https://mp.weixin.qq.com/s?__biz=MjM5NjQ5MzgxMg==&mid=2652260532&idx=1&sn=6f3392d9114bebc8402f7d88e56ec611&chksm=bd0a882f8a7d0139ed533ab0dd8d1ad32ccc5e55db923e74226de9361f2d396fcfec856d5764&scene=27' },
      { link: 'https://cs.cug.edu.cn/info/1017/3990.htm' },
      { link: 'https://www.mcm.edu.cn/upload_cn/node/626/n7nhofCn201ab6d8d92db35564daa7cca31853d6.pdf' },
      { link: 'http://gjcxcy.bjtu.edu.cn/NewLXItemListForStudentDetail.aspx?ItemNo=731490' },
      { link: 'https://jzzx.cug.edu.cn/info/1002/2236.htm' }
    ];

    if (links[index] && links[index][linkType]) {
      return links[index][linkType];
    }
    return '#';
  }

  setupLanguageSwitcher() {
    const langSwitcher = document.getElementById('lang-switcher');
    if (langSwitcher) {
      langSwitcher.addEventListener('click', (e) => {
        e.preventDefault();
        const newLang = this.currentLang === 'en' ? 'zh' : 'en';
        this.switchLanguage(newLang);
      });
    }
  }
}

// 导出单例
let i18nInstance = null;

export default function initI18n() {
  if (!i18nInstance) {
    i18nInstance = new I18n();
  }
  return i18nInstance;
}
